/**
 * iter252a — Modern WhatsApp/iPhone-style ringtones using Web Audio API.
 *
 * Two distinct sounds:
 *   - createOutgoingRingback()  — caller side. Slow pulse (~1Hz),
 *     two-tone modern sound (440/523Hz) like WhatsApp's outgoing ring.
 *   - createIncomingRingtone()  — callee side. Faster melody arpeggio
 *     (E5-G5-B5-G5), repeating every 1.4s, iPhone "Marimba"-inspired.
 *
 * Both APIs return `{ play(), stop() }`. Calling .stop() releases the
 * AudioContext, so callers MUST stop before garbage-collecting.
 *
 * No binary files needed — keeps the bundle slim and works offline.
 */

function pickContextClass() {
  // Safari pre-2024 still required webkitAudioContext.
  return window.AudioContext || window.webkitAudioContext || null;
}

function _envelope(gain, t0, attack = 0.02, decay = 0.18, sustain = 0.0001) {
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(sustain, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
}

function _tone(ctx, dest, freq, when, dur, vol = 0.15) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  osc.connect(gain).connect(dest);
  _envelope(gain, when, 0.015, dur, vol);
  osc.start(when);
  osc.stop(when + dur + 0.05);
}

/**
 * Outgoing call: soft two-tone "tu-tuuu" looped every ~3s
 * (mimics WhatsApp + modern smartphones).
 */
export function createOutgoingRingback() {
  let ctx = null;
  let loop = null;
  let stopped = false;

  function scheduleBurst(at) {
    if (!ctx || stopped) return;
    const out = ctx.destination;
    // Two soft tones, 220ms + 350ms
    _tone(ctx, out, 440, at, 0.22, 0.12);
    _tone(ctx, out, 523.25, at + 0.18, 0.35, 0.14);
  }

  function play() {
    if (ctx) return;
    const Ctx = pickContextClass();
    if (!Ctx) return;
    ctx = new Ctx();
    // Resume on user gesture (Chrome autoplay)
    if (ctx.state === 'suspended') ctx.resume().catch(() => { /* no-op */ });
    stopped = false;
    const tick = () => {
      if (!ctx || stopped) return;
      const now = ctx.currentTime;
      scheduleBurst(now);
      loop = setTimeout(tick, 3000);    // 3s gap between rings
    };
    tick();
  }

  function stop() {
    stopped = true;
    if (loop) clearTimeout(loop);
    loop = null;
    if (ctx) {
      try { ctx.close(); } catch { /* no-op */ }
      ctx = null;
    }
  }

  return { play, stop };
}

/**
 * Incoming call: short marimba-style melody, repeating every 1.4s.
 * Notes (Hz): E5 = 659.25, G5 = 783.99, B5 = 987.77, G5 = 783.99.
 */
export function createIncomingRingtone() {
  let ctx = null;
  let loop = null;
  let stopped = false;

  const NOTES = [659.25, 783.99, 987.77, 783.99];

  function playMelody(at) {
    if (!ctx || stopped) return;
    const out = ctx.destination;
    let t = at;
    for (const f of NOTES) {
      _tone(ctx, out, f, t, 0.16, 0.18);
      t += 0.16;
    }
  }

  function play() {
    if (ctx) return;
    const Ctx = pickContextClass();
    if (!Ctx) return;
    ctx = new Ctx();
    if (ctx.state === 'suspended') ctx.resume().catch(() => { /* no-op */ });
    stopped = false;
    const tick = () => {
      if (!ctx || stopped) return;
      const now = ctx.currentTime;
      playMelody(now);
      loop = setTimeout(tick, 1400);   // 1.4s loop
    };
    tick();
  }

  function stop() {
    stopped = true;
    if (loop) clearTimeout(loop);
    loop = null;
    if (ctx) {
      try { ctx.close(); } catch { /* no-op */ }
      ctx = null;
    }
  }

  return { play, stop };
}
